import { useMemo } from 'react';

import type { MigrationDetailResponse } from '../api/migrations';
import { useLicenseBalance } from '../components/LicenseBalancePanel';
import { useMintReadiness } from '../components/MintReadinessPanel';
import { useTenantDetailQuery } from './useTenantDetail';
import { CampaignPreflight, deriveCampaignPreflight } from '../lib/campaignPreflight';

// FD-493 — assembles the four inputs `deriveCampaignPreflight` needs, and does
// NOTHING ELSE.
//
// ── THE JUDGEMENT IS NOT HERE, AND MUST NOT MOVE HERE ──────────────────────
//
// `lib/campaignPreflight.ts` owns every rule: which absence is `unknown` rather
// than `fail`, why a Hybrid source tenant is `not-applicable`, why an
// `insufficient` consent verdict is amber rather than red, and why the pool
// bucket earns a `fail` where the consumable one does not. That module is a pure
// function asserted against fixtures; this hook is plumbing. A second reader of
// the same rule is how the two disagree on the day it matters
// (`devicePhase.ts`'s reason, restated here because the next person to reach for
// a shortcut will open this file rather than that one).
//
// ── EVERY HOOK IS CALLED UNCONDITIONALLY ───────────────────────────────────
//
// The migration detail arrives asynchronously, so the tenant ids these queries
// key on are undefined on the first render and present on the next. Both tenant
// queries therefore run on EVERY render with a possibly-undefined id and gate
// themselves on `enabled`, rather than being called only once the detail
// resolves. That is FD-188: a hook whose invocation depends on route or data
// state changes the rendered hook count and unmounts the tree.
//
// ── THE FETCH POLICY IS INHERITED, NOT RE-DECIDED ──────────────────────────
//
// `useLicenseBalance` and `useMintReadiness` carry their own explicit
// "on mount, never on a poll, never on focus, never on reconnect" settings, for
// reasons their own files state at length: ATC Licensing and the Graph probe are
// real cold-start reads against services this console does not own. This hook
// passes `enabled` and changes nothing else, so the preflight page cannot
// quietly become the surface that puts them on a loop.

export interface CampaignPreflightState {
  /** `null` until the migration record has arrived. Never a half-built view. */
  preflight: CampaignPreflight | null;
  /**
   * At least one live probe is still in flight.
   *
   * Surfaced so the page can say "still checking" rather than presenting the
   * `unknown` rows a not-yet-answered probe legitimately produces as if they
   * were verdicts. Those two states look identical on a row and are not the same
   * thing — the FD-246 discipline, applied to a moment rather than to a value.
   */
  probesPending: boolean;
}

export function useCampaignPreflight(
  migrationId: string | undefined,
  detail: MigrationDetailResponse | undefined,
): CampaignPreflightState {
  const enabled = Boolean(migrationId);
  const migration = detail?.migration;
  const devices = detail?.devices;

  const licenseBalance = useLicenseBalance(enabled);
  const mintReadiness = useMintReadiness(migrationId ?? '', enabled);

  // Only Cloud has a source tenant. Asking the registry for a Hybrid one would
  // be asking about a record that cannot exist — `Resolve-MigrationTenant`
  // answers `400 D2C_HAS_NO_SOURCE_TENANT` for the same reason — and the check
  // is `not-applicable` regardless of what came back.
  const sourceTenant = useTenantDetailQuery(
    migration?.migrationType === 'T2T' ? (migration.sourceTenantId ?? undefined) : undefined,
  );
  const destTenant = useTenantDetailQuery(migration?.destTenantId ?? undefined);

  const preflight = useMemo(() => {
    if (!migration) return null;
    return deriveCampaignPreflight({
      migration,
      devices: devices ?? [],
      // `?? null` rather than letting `undefined` through: the lib treats both
      // the same, but `null` is what its `PreflightInputs` documents as "the
      // read failed, or licensing is off in this deployment", and saying it
      // explicitly keeps the two ends agreeing about what absence means.
      licenseBalance: licenseBalance.data ?? null,
      mintReadiness: mintReadiness.data ?? null,
      sourceTenant: sourceTenant.data ?? null,
      destTenant: destTenant.data ?? null,
    });
  }, [
    migration,
    devices,
    licenseBalance.data,
    mintReadiness.data,
    sourceTenant.data,
    destTenant.data,
  ]);

  return {
    preflight,
    probesPending:
      licenseBalance.isLoading ||
      mintReadiness.isLoading ||
      sourceTenant.isLoading ||
      destTenant.isLoading,
  };
}
